// SPDX-License-Identifier: Apache-2.0
// File: src/ui/components/FindSoundsModal.tsx
import React, { useMemo, useRef, useState } from 'react';
import { synthEngine } from '../../audio/AudioEngineController';
import { webMidiController } from '../../audio/WebMidiController';
import { parseDx7SysexBank } from '../../io/SysexAndExportPipeline';
import {
  INSTRUMENT_CATEGORIES,
  SIXTEEN_CUSTOMIZER_PARAM_IDS,
  saveUserPresetSlotToStorage,
  type InstrumentCategoryId,
  type SynthPreset,
} from '../../presets/FactorySoundLibrary';
import {
  ALL_ASSIGNABLE_PARAMS,
  PocketGridRetroPalette,
  type SynthParamId,
} from '../theme/PocketGridRetroTheme';
import { PrecisionHorizontalSlider } from './PrecisionHorizontalSlider';

interface FindSoundsModalProps {
  presets: SynthPreset[];
  activePreset: SynthPreset;
  paramValues: Record<string, number>;
  paramToCcMap: Record<string, number>;
  learningParamId: string | null;
  onToggleMidiLearnForParam: (paramId: string) => void;
  onParamChange: (paramId: string, value: number) => void;
  onSelectPreset: (preset: SynthPreset) => void;
  onSaveUserSlotPreset: (updatedPreset: SynthPreset) => void;
  onToggleFavorite: (presetId: string) => void;
  onImportSysexPresets: (newPresets: SynthPreset[]) => void;
  onOpenFineTune?: (preset: SynthPreset) => void;
  onClose: () => void;
}

type QuickFilterMode = 'ALL' | 'FAVORITES' | 'USER' | 'SYX';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
function formatMidiNote(midi: number): string {
  const name = NOTE_NAMES[((midi % 12) + 12) % 12];
  const oct = Math.floor(midi / 12) - 1;
  return `${name}${oct}`;
}

export const FindSoundsModal: React.FC<FindSoundsModalProps> = ({
  presets,
  activePreset,
  paramValues,
  paramToCcMap,
  learningParamId,
  onToggleMidiLearnForParam,
  onParamChange,
  onSelectPreset,
  onSaveUserSlotPreset,
  onToggleFavorite,
  onImportSysexPresets,
  onOpenFineTune,
  onClose,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<InstrumentCategoryId>(
    activePreset.category || 'KEYS_PIANOS'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState<QuickFilterMode>('ALL');
  const [previewPreset, setPreviewPreset] = useState<SynthPreset>(activePreset);
  const [customSoundName, setCustomSoundName] = useState<string>(activePreset.name);
  const [targetUserSlotNum, setTargetUserSlotNum] = useState<number>(
    activePreset.userSlotIndex ?? 1
  );
  const [auditionActiveNotes, setAuditionActiveNotes] = useState<Set<number>>(new Set());
  const [statusBanner, setStatusBanner] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const currentCatMeta = useMemo(
    () =>
      INSTRUMENT_CATEGORIES.find((c) => c.id === selectedCategory) || INSTRUMENT_CATEGORIES[0],
    [selectedCategory]
  );

  // Separate the 8 Most Popular Factory Sounds and the 4 User-Created Slots for the selected category
  const { popularEightPresets, fourUserSlots } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matching = presets.filter((p) => {
      if (filterMode === 'FAVORITES' && !p.favorite) return false;
      if (filterMode === 'SYX' && !p.isSyx && p.category !== 'IMPORTED_SYX') return false;
      if (filterMode === 'USER' && !p.isUserSlot) return false;
      if (!q && filterMode === 'ALL' && p.category !== selectedCategory) return false;
      if (q) {
        const hay = `${p.name} ${p.engineBadge} ${p.featureBadge} ${p.tags.join(' ')}`.toLowerCase();
        return hay.includes(q);
      }
      return true;
    });

    const popular = matching.filter((p) => !p.isUserSlot).slice(0, 8);
    const userSlots = presets
      .filter((p) => p.category === selectedCategory && p.isUserSlot)
      .slice(0, 4);

    return { popularEightPresets: popular, fourUserSlots: userSlots };
  }, [presets, selectedCategory, searchQuery, filterMode]);

  const handleCardClick = async (preset: SynthPreset) => {
    setPreviewPreset(preset);
    setCustomSoundName(
      preset.isUserSlot && !preset.isPopulatedUserSlot
        ? `Custom ${currentCatMeta.shortLabel} ${preset.userSlotIndex ?? 1}`
        : preset.name.replace(/^\d+\.\s*/, '')
    );
    if (preset.userSlotIndex) {
      setTargetUserSlotNum(preset.userSlotIndex);
    }
    onSelectPreset(preset);

    // Audition a note (or drum hit for drum kits)
    if (preset.targetEngineId === 1) {
      await synthEngine.triggerDrum(0, 0.9);
      window.setTimeout(() => void synthEngine.triggerDrum(1, 0.85), 180);
    } else {
      const testNote = preset.category === 'BASSES' ? 38 : 62;
      await synthEngine.noteOn(
        testNote,
        0.84,
        999,
        1,
        preset.targetEngineId,
        preset.opRatios,
        preset.opLevels,
        preset.params
      );
      window.setTimeout(() => synthEngine.noteOff(testNote, 999), 360);
    }
  };

  const handleLoadCategoryStartingSound = async () => {
    const firstSlot = fourUserSlots[targetUserSlotNum - 1] || fourUserSlots[0];
    if (!firstSlot) return;
    await handleCardClick(firstSlot);
    setStatusBanner(
      `Loaded starting sound template "${currentCatMeta.initTemplateName}" — tweak the 16 sliders below and click SAVE TO USER SLOT ${targetUserSlotNum}!`
    );
  };

  const handleSaveToUserSlot = (slotNum: number) => {
    const slotPreset = fourUserSlots.find((s) => s.userSlotIndex === slotNum) || fourUserSlots[0];
    if (!slotPreset) return;

    const cleanTitle =
      customSoundName.trim() || `User ${currentCatMeta.shortLabel} ${slotNum}`;

    // Snapshot all 16 customized slider parameters + current preset operator ratios/levels
    const updatedParams: Partial<Record<SynthParamId, number>> = {};
    for (const pid of SIXTEEN_CUSTOMIZER_PARAM_IDS) {
      updatedParams[pid] = paramValues[pid];
    }

    const updatedUserPreset: SynthPreset = {
      ...slotPreset,
      name: cleanTitle,
      engineBadge: 'USER CUSTOM',
      featureBadge: `SAVED USER #${slotNum}`,
      tags: ['User Custom', currentCatMeta.shortLabel],
      favorite: true,
      isUserSlot: true,
      userSlotIndex: slotNum,
      isPopulatedUserSlot: true,
      targetEngineId: previewPreset.targetEngineId,
      opRatios: [...previewPreset.opRatios] as [number, number, number, number, number, number],
      opLevels: [...previewPreset.opLevels] as [number, number, number, number, number, number],
      params: updatedParams,
    };

    saveUserPresetSlotToStorage(updatedUserPreset);
    onSaveUserSlotPreset(updatedUserPreset);
    setPreviewPreset(updatedUserPreset);
    onSelectPreset(updatedUserPreset);
    setStatusBanner(
      `✓ Saved "${cleanTitle}" into ${currentCatMeta.label} -> USER SLOT ${slotNum}!`
    );
  };

  const handleSyxFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const imported = parseDx7SysexBank(buf);
      onImportSysexPresets(imported);
      setSelectedCategory('IMPORTED_SYX');
      setFilterMode('ALL');
      setStatusBanner(`Imported ${imported.length} voices from ${file.name}`);
    } catch (err) {
      setStatusBanner(err instanceof Error ? err.message : 'Invalid .syx file');
    }
  };

  const [auditionOctave, setAuditionOctave] = useState<number>(0);
  const auditionIntervals = [
    { semitone: 0, label: 'ROOT' },
    { semitone: 2, label: '2ND' },
    { semitone: 4, label: '3RD' },
    { semitone: 7, label: '5TH' },
    { semitone: 12, label: 'OCT' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: PocketGridRetroPalette.chassisGunmetal,
        color: PocketGridRetroPalette.warmIvoryText,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        border: `2px solid ${PocketGridRetroPalette.panelBorder}`,
      }}
    >
      {/* Hidden .SYX File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".syx"
        style={{ display: 'none' }}
        onChange={handleSyxFileUpload}
      />

      {/* TOP HEADER BAR */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          background: '#18191C',
          borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={onClose}
          style={{
            minHeight: '34px',
            padding: '0 12px',
            borderRadius: '6px',
            background: PocketGridRetroPalette.brushedTitanium,
            border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            color: PocketGridRetroPalette.warmIvoryText,
            fontWeight: 700,
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          ◄ BACK TO WORKSTATION
        </button>

        {/* Live Search Input */}
        <div
          style={{
            flex: 1,
            minWidth: '180px',
            display: 'flex',
            alignItems: 'center',
            background: PocketGridRetroPalette.recessedTrack,
            border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            borderRadius: '6px',
            padding: '0 10px',
            height: '34px',
          }}
        >
          <span style={{ color: PocketGridRetroPalette.mutedPewterText, marginRight: '6px' }}>
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search popular sounds, instruments, user patches..."
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: PocketGridRetroPalette.warmIvoryText,
              fontSize: '12px',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: PocketGridRetroPalette.mutedPewterText,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter Pills */}
        {(
          [
            { id: 'ALL', label: '8 POPULAR + 4 USER' },
            { id: 'FAVORITES', label: '★ FAVORITES' },
            { id: 'USER', label: '👤 USER SLOTS' },
            { id: 'SYX', label: '.SYX BANKS' },
          ] as { id: QuickFilterMode; label: string }[]
        ).map((pill) => {
          const active = filterMode === pill.id;
          return (
            <button
              key={pill.id}
              onClick={() => setFilterMode(active ? 'ALL' : pill.id)}
              style={{
                minHeight: '34px',
                padding: '0 10px',
                borderRadius: '6px',
                background: active ? '#322B20' : PocketGridRetroPalette.brushedTitanium,
                border: `1px solid ${
                  active ? PocketGridRetroPalette.tungstenAmber : PocketGridRetroPalette.panelBorder
                }`,
                color: active
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.warmIvoryText,
                fontWeight: 600,
                fontSize: '11px',
                cursor: 'pointer',
              }}
            >
              {pill.label}
            </button>
          );
        })}

        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            minHeight: '34px',
            padding: '0 10px',
            borderRadius: '6px',
            background: PocketGridRetroPalette.brushedTitanium,
            border: `1px solid ${PocketGridRetroPalette.champagneBrass}`,
            color: PocketGridRetroPalette.champagneBrass,
            fontWeight: 600,
            fontSize: '11px',
            cursor: 'pointer',
          }}
        >
          + IMPORT .SYX
        </button>

        <button
          onClick={() => {
            onSelectPreset(previewPreset);
            onClose();
          }}
          style={{
            minHeight: '34px',
            padding: '0 14px',
            borderRadius: '6px',
            background: `linear-gradient(180deg, ${PocketGridRetroPalette.phosphorMoss} 0%, #37734B 100%)`,
            border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
            color: PocketGridRetroPalette.incandescentCream,
            fontWeight: 700,
            fontSize: '11px',
            cursor: 'pointer',
            boxShadow: `0 0 12px ${PocketGridRetroPalette.tungstenAmber}55`,
          }}
        >
          USE SOUND IN SYNTH ►
        </button>
      </div>

      {statusBanner && (
        <div
          style={{
            padding: '4px 14px',
            background: '#2C2418',
            color: PocketGridRetroPalette.incandescentCream,
            fontSize: '11px',
            fontWeight: 700,
            borderBottom: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
          }}
        >
          {statusBanner}
        </div>
      )}

      {/* MAIN CONTENT BODY (LEFT INSTRUMENT BANK SIDEBAR + RIGHT: 8 POPULAR + 4 USER SLOTS + 16 CUSTOMIZER SLIDERS) */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
        {/* LEFT COLUMN: 8 INSTRUMENT BANKS */}
        <div
          style={{
            width: '195px',
            background: '#1A1B1E',
            borderRight: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            overflowY: 'auto',
          }}
        >
          {INSTRUMENT_CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat.id && filterMode === 'ALL' && !searchQuery;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setFilterMode('ALL');
                  setSearchQuery('');
                }}
                style={{
                  minHeight: '38px',
                  padding: '0 10px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: isSelected ? '#2C2419' : PocketGridRetroPalette.brushedTitanium,
                  border: `1.5px solid ${
                    isSelected
                      ? PocketGridRetroPalette.tungstenAmber
                      : PocketGridRetroPalette.panelBorder
                  }`,
                  color: isSelected
                    ? PocketGridRetroPalette.warmIvoryText
                    : PocketGridRetroPalette.mutedPewterText,
                  fontWeight: 700,
                  fontSize: '11px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>
                  <span style={{ marginRight: '6px', color: PocketGridRetroPalette.tungstenAmber }}>
                    {cat.icon}
                  </span>
                  {cat.label}
                </span>
                {isSelected && (
                  <span
                    style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      background: PocketGridRetroPalette.incandescentCream,
                    }}
                  />
                )}
              </button>
            );
          })}

          {/* Quick Starting Sound Button for Selected Bank */}
          <button
            onClick={handleLoadCategoryStartingSound}
            style={{
              marginTop: 'auto',
              padding: '8px',
              borderRadius: '6px',
              background: '#1F2A26',
              border: `1px solid ${PocketGridRetroPalette.vintageVfdSage}`,
              color: PocketGridRetroPalette.incandescentCream,
              fontWeight: 700,
              fontSize: '10px',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            ✨ LOAD STARTING {currentCatMeta.shortLabel.toUpperCase()} TEMPLATE
          </button>
        </div>

        {/* RIGHT PANEL: (1) 8 POPULAR SOUNDS + 4 USER SLOTS, (2) 16 CUSTOMIZER SLIDERS, (3) AUDITION KEYBED */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflowY: 'auto',
            padding: '8px 12px',
            gap: '8px',
          }}
        >
          {/* SECTION A: 8 MOST POPULAR SOUNDS FOR THIS BANK */}
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 800,
                color: PocketGridRetroPalette.tungstenAmber,
                marginBottom: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                {currentCatMeta.icon} {currentCatMeta.label} — 8 MOST POPULAR STUDIO SOUNDS (CLICK
                ANY SOUND TO AUDITION OR CUSTOMIZE BELOW)
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '6px',
              }}
            >
              {popularEightPresets.map((preset) => {
                const isSelected = previewPreset.id === preset.id;
                return (
                  <div
                    key={preset.id}
                    onClick={() => void handleCardClick(preset)}
                    style={{
                      padding: '7px 9px',
                      borderRadius: '6px',
                      background: isSelected ? '#2E261C' : PocketGridRetroPalette.brushedTitanium,
                      border: `1.5px solid ${
                        isSelected
                          ? PocketGridRetroPalette.tungstenAmber
                          : PocketGridRetroPalette.panelBorder
                      }`,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '4px',
                      boxShadow: isSelected
                        ? `0 0 10px ${PocketGridRetroPalette.tungstenAmber}44`
                        : 'none',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: PocketGridRetroPalette.warmIvoryText,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {preset.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFavorite(preset.id);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: preset.favorite
                            ? PocketGridRetroPalette.tungstenAmber
                            : PocketGridRetroPalette.mutedPewterText,
                          fontSize: '13px',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        {preset.favorite ? '★' : '☆'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '1px 5px',
                          borderRadius: '3px',
                          background: '#2D2418',
                          color: PocketGridRetroPalette.tungstenAmber,
                        }}
                      >
                        {preset.engineBadge}
                      </span>
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '1px 5px',
                          borderRadius: '3px',
                          background: '#1C2926',
                          color: PocketGridRetroPalette.vintageVfdSage,
                        }}
                      >
                        {preset.featureBadge}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION B: 4 USER-CREATED SOUND SLOTS FOR THIS BANK + SAVE BAR */}
          <div
            style={{
              background: '#1A1C20',
              borderRadius: '8px',
              border: `1px solid ${PocketGridRetroPalette.vintageVfdSage}88`,
              padding: '7px 10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.vintageVfdSage,
                }}
              >
                👤 4 USER-CREATED SOUND SLOTS ({currentCatMeta.label}) — START FROM INIT TEMPLATE OR
                SAVE CUSTOMIZED SOUND:
              </span>

              {/* Save Custom Sound Name + Slot Button Bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="text"
                  value={customSoundName}
                  onChange={(e) => setCustomSoundName(e.target.value)}
                  placeholder="Custom Sound Name..."
                  style={{
                    height: '26px',
                    padding: '0 8px',
                    borderRadius: '4px',
                    background: '#121315',
                    border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.warmIvoryText,
                    fontSize: '11px',
                    fontWeight: 600,
                    width: '165px',
                  }}
                />
                {[1, 2, 3, 4].map((slotNum) => (
                  <button
                    key={slotNum}
                    onClick={() => handleSaveToUserSlot(slotNum)}
                    style={{
                      height: '26px',
                      padding: '0 8px',
                      borderRadius: '4px',
                      background:
                        targetUserSlotNum === slotNum
                          ? PocketGridRetroPalette.phosphorMoss
                          : '#262A2E',
                      border: `1px solid ${PocketGridRetroPalette.phosphorMoss}`,
                      color: PocketGridRetroPalette.incandescentCream,
                      fontSize: '10px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    💾 SAVE SLOT {slotNum}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
              {fourUserSlots.map((uSlot, idx) => {
                const slotNum = uSlot.userSlotIndex ?? idx + 1;
                const isSelected = previewPreset.id === uSlot.id;
                return (
                  <div
                    key={uSlot.id}
                    onClick={() => {
                      setTargetUserSlotNum(slotNum);
                      void handleCardClick(uSlot);
                    }}
                    style={{
                      padding: '6px 9px',
                      borderRadius: '6px',
                      background: isSelected
                        ? '#1F2F2A'
                        : uSlot.isPopulatedUserSlot
                        ? '#222826'
                        : '#16171A',
                      border: `1.5px ${uSlot.isPopulatedUserSlot ? 'solid' : 'dashed'} ${
                        isSelected
                          ? PocketGridRetroPalette.incandescentCream
                          : PocketGridRetroPalette.vintageVfdSage
                      }`,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: uSlot.isPopulatedUserSlot
                          ? PocketGridRetroPalette.incandescentCream
                          : PocketGridRetroPalette.vintageVfdSage,
                      }}
                    >
                      <span>{uSlot.name}</span>
                      <span style={{ fontSize: '9px' }}>SLOT #{slotNum}</span>
                    </div>
                    <span
                      style={{
                        fontSize: '9px',
                        color: PocketGridRetroPalette.mutedPewterText,
                      }}
                    >
                      {uSlot.isPopulatedUserSlot
                        ? 'Custom User Patch (Click to play/edit)'
                        : `Click for "${currentCatMeta.initTemplateName}" starting sound`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION C: 16 MOST POPULARLY CUSTOMIZED PARAMETERS AS HORIZONTAL SLIDERS */}
          <div
            style={{
              flex: 1,
              background: PocketGridRetroPalette.brushedTitanium,
              borderRadius: '8px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                paddingBottom: '4px',
                marginBottom: '6px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: PocketGridRetroPalette.warmIvoryText,
                }}
              >
                🎛 16 SOUND CUSTOMIZER SLIDERS — TWEAK PREMADE OR STARTING SOUND & SAVE TO USER SLOT
                ABOVE
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '10px', color: PocketGridRetroPalette.mutedPewterText }}>
                  Editing: <strong style={{ color: PocketGridRetroPalette.tungstenAmber }}>{previewPreset.name}</strong>
                </span>
                {onOpenFineTune && (
                  <button
                    onClick={() => onOpenFineTune(previewPreset)}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: '#24211A',
                      border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                      color: PocketGridRetroPalette.tungstenAmber,
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    🔧 Fine Tune Sound
                  </button>
                )}
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '6px 14px',
                alignItems: 'center',
              }}
            >
              {SIXTEEN_CUSTOMIZER_PARAM_IDS.map((paramId, idx) => {
                const meta = ALL_ASSIGNABLE_PARAMS[paramId];
                const val = paramValues[paramId] ?? meta.defaultValue;
                const accent =
                  idx < 4
                    ? PocketGridRetroPalette.tungstenAmber
                    : idx < 8
                    ? PocketGridRetroPalette.vintageVfdSage
                    : idx < 12
                    ? PocketGridRetroPalette.burntTerracotta
                    : PocketGridRetroPalette.champagneBrass;
                return (
                  <PrecisionHorizontalSlider
                    key={paramId}
                    label={meta.label}
                    formattedValue={meta.formatDisplay(val)}
                    value={val}
                    defaultValue={meta.defaultValue}
                    accentColor={accent}
                    midiCcNumber={paramToCcMap[paramId]}
                    isMidiLearning={learningParamId === paramId}
                    onToggleMidiLearn={() => onToggleMidiLearnForParam(paramId)}
                    onChange={(v) => {
                      webMidiController.notifyParameterTouched(paramId);
                      onParamChange(paramId, v);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* SECTION D: 5-KEY TACTILE AUDITION STRIP */}
          <div
            style={{
              height: '42px',
              minHeight: '42px',
              maxHeight: '42px',
              background: '#141518',
              borderRadius: '6px',
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              display: 'flex',
              padding: '2px 8px',
              gap: '8px',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '10px', fontWeight: 800, color: PocketGridRetroPalette.tungstenAmber }}>
                AUDITION:
              </span>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#1F2024',
                  border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => setAuditionOctave((o) => Math.max(-2, o - 1))}
                  title="Audition down 1 octave"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: PocketGridRetroPalette.warmIvoryText,
                    padding: '2px 7px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: '12px',
                  }}
                >
                  −
                </button>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    padding: '0 4px',
                    color:
                      auditionOctave !== 0
                        ? PocketGridRetroPalette.tungstenAmber
                        : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  OCT {auditionOctave >= 0 ? `+${auditionOctave}` : auditionOctave}
                </span>
                <button
                  onClick={() => setAuditionOctave((o) => Math.min(2, o + 1))}
                  title="Audition up 1 octave"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: PocketGridRetroPalette.warmIvoryText,
                    padding: '2px 7px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    fontSize: '12px',
                  }}
                >
                  +
                </button>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '4px',
                flex: 1,
                maxWidth: '420px',
                height: '100%',
                alignItems: 'center',
              }}
            >
              {auditionIntervals.map((k) => {
                const note = 48 + k.semitone + auditionOctave * 12;
                const active = auditionActiveNotes.has(note);
                const noteName = formatMidiNote(note);
                return (
                  <button
                    key={note}
                    onPointerDown={(e) => {
                      try {
                        e.currentTarget.setPointerCapture(e.pointerId);
                      } catch {}
                      setAuditionActiveNotes((prev) => new Set(prev).add(note));
                      void synthEngine.noteOn(
                        note,
                        0.85,
                        note,
                        1,
                        previewPreset.targetEngineId,
                        previewPreset.opRatios,
                        previewPreset.opLevels,
                        previewPreset.params
                      );
                    }}
                    onPointerUp={() => {
                      setAuditionActiveNotes((prev) => {
                        const next = new Set(prev);
                        next.delete(note);
                        return next;
                      });
                      synthEngine.noteOff(note, note);
                    }}
                    onPointerCancel={() => {
                      setAuditionActiveNotes((prev) => {
                        const next = new Set(prev);
                        next.delete(note);
                        return next;
                      });
                      synthEngine.noteOff(note, note);
                    }}
                    onPointerLeave={() => {
                      setAuditionActiveNotes((prev) => {
                        const next = new Set(prev);
                        next.delete(note);
                        return next;
                      });
                      synthEngine.noteOff(note, note);
                    }}
                    style={{
                      flex: 1,
                      height: '34px',
                      borderRadius: '5px',
                      background: active
                        ? PocketGridRetroPalette.tungstenAmber
                        : PocketGridRetroPalette.warmIvoryText,
                      border: `1.5px solid ${active ? '#FFF' : '#333'}`,
                      color: active ? '#141517' : '#1E1F22',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      touchAction: 'none',
                      boxShadow: active
                        ? '0 0 10px rgba(217, 148, 59, 0.9)'
                        : 'inset 0 -2px 4px rgba(0,0,0,0.25)',
                    }}
                  >
                    <span style={{ fontSize: '11px', fontWeight: 900, lineHeight: 1 }}>{noteName}</span>
                    <span
                      style={{
                        fontSize: '7.5px',
                        fontWeight: 700,
                        opacity: 0.75,
                        lineHeight: 1,
                        marginTop: '1px',
                      }}
                    >
                      {k.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <span style={{ fontSize: '9px', color: PocketGridRetroPalette.mutedPewterText }}>
              Tap keys to audition sound
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
